import {
  fromAgentEntity,
  toAgentListItem,
  toCreateAgentDto,
  toUpdateAgentDto,
  type AgentEntity,
} from '@/features/agents/adapters/agent-api-adapter'
import type { AgentConfig } from '@/features/agents/schema'
import { httpClient } from '@/lib/api'

export type AgentFilters = {
  model?: string
  language?: string
  isActive?: boolean
}

export type TestAgentResponse = {
  agentId: number
  agentName: string
  userMessage: string
  agentResponse: string
  model: string
  processingTime: number
  tokensUsed: number
  stage: string
  reasoning: string[]
}

export type UploadFileResponse = {
  id: string
  name: string
  size: number
  url: string
}

export class AgentService {
  public static async list(params: AgentFilters = {}) {
    const { data } = await httpClient().get<AgentEntity[]>('/agents', {
      params,
    })
    return data.map(toAgentListItem)
  }

  public static async create(payload: AgentConfig) {
    const dto = toCreateAgentDto(payload)
    const { data } = await httpClient().post<AgentEntity>('/agents', dto)
    return fromAgentEntity(data)
  }

  public static async update(id: number, payload: Partial<AgentConfig>) {
    const dto = toUpdateAgentDto(payload)
    const { data } = await httpClient().patch<AgentEntity>(
      `/agents/${id}`,
      dto
    )
    return fromAgentEntity(data)
  }

  public static async get(id: number) {
    const { data } = await httpClient().get<AgentEntity>(`/agents/${id}`)
    return fromAgentEntity(data)
  }

  public static async delete(id: number) {
    await httpClient().delete(`/agents/${id}`)
  }

  public static async toggle(id: number, isActive: boolean) {
    await httpClient().patch(`/agents/${id}/toggle`, { isActive })
  }

  public static async setDefault(id: number, isDefault: boolean) {
    const { data } = await httpClient().patch<AgentEntity>(
      `/agents/${id}/default`,
      { isDefault }
    )
    return fromAgentEntity(data)
  }

  public static async duplicate(id: number) {
    const { data } = await httpClient().post<AgentEntity>(
      `/agents/${id}/duplicate`
    )
    return fromAgentEntity(data)
  }

  public static async test(id: number, message: string) {
    const { data } = await httpClient().post<TestAgentResponse>(
      `/agents/${id}/test`,
      { message }
    )
    return data
  }

  public static async train(id: number) {
    await httpClient().post(`/agents/${id}/train`)
  }

  public static async uploadFile(agentId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)

    const { data } = await httpClient().post<UploadFileResponse>(
      `/agents/${agentId}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return data
  }

  public static async deleteFile(id: string) {
    await httpClient().delete(`/agents/files/${id}`)
  }
}
